import React from 'react';
import { useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/yoshi-flow-editor/tpa-settings/react';
import { TPAComponentsProvider } from 'wix-ui-tpa/TPAComponentsConfig';
import { st, classes } from './Widget.st.css';
import EmptyState from './EmptyState';
import SubscriptionsList from './SubscriptionsList';
import RuntimeContext, { connect } from '../../../common/components/runtime-context';
import { TabState } from '../../../types/settings';
import { RootState } from '../state';
import CancelConfirmModal from './CancelConfirmModal';
import settingsParams from '../settingsParams';
import { EXPERIMENT_USE_BASS_API } from '../constants';
import SubscriptionsListBass from './SubscriptionsListBass';

type RuntimeProps = ReturnType<typeof mapRuntimeToProps>;

interface WidgetWrapperProps {
  tabState: TabState;
  ppInstalled: boolean;
}

interface ContextParams {
  methods: Partial<any>;
  host: Partial<any>;
  state: RootState;
}

export default function (props: WidgetWrapperProps & ContextParams & RuntimeProps) {
  const { state, methods, host, tabState, ppInstalled } = props;
  const { isMobile } = useEnvironment();
  return (
    <TPAComponentsProvider value={{ mobile: isMobile }}>
      <RuntimeContext.Provider value={{ state, actions: methods, host }}>
        <Widget tabState={tabState} ppInstalled={ppInstalled} />
      </RuntimeContext.Provider>
    </TPAComponentsProvider>
  );
}

const WidgetComponent = ({ tabState, ppInstalled, subscriptions }: WidgetWrapperProps & RuntimeProps) => {
  const settings = useSettings();
  const { experiments } = useExperiments();
  const { isMobile } = useEnvironment();
  const SubscriptionListComponent = experiments.enabled(EXPERIMENT_USE_BASS_API)
    ? SubscriptionsListBass
    : SubscriptionsList;
  return (
    <div
      role="region"
      aria-labelledby="my-subscriptions-title"
      className={st(classes.root, isMobile ? { mobile: isMobile } : undefined)}
      data-hook="MySubscriptions-wrapper"
    >
      {!isMobile && (
        <div className={classes.header}>
          <h1 id="my-subscriptions-title" data-hook="app-page-title">
            {settings.get(settingsParams.pageTitleText)}
          </h1>
          <p data-hook="app-page-description">{settings.get(settingsParams.pageDescriptionText)}</p>
        </div>
      )}
      <div className={classes.content}>
        {!subscriptions || subscriptions.entities.length === 0 || tabState === TabState.Empty ? (
          <EmptyState ppInstalled={ppInstalled} />
        ) : (
          <>
            <SubscriptionListComponent subscriptions={subscriptions.entities} />
            <CancelConfirmModal />
          </>
        )}
      </div>
    </div>
  );
};

const mapRuntimeToProps = (state: RootState, _: WidgetWrapperProps, __: any) => ({
  subscriptions: state.subscriptions,
});

export const Widget = connect(mapRuntimeToProps)(WidgetComponent);
